import { FieldEntity, IField } from '../field2.data-model';
import {
  FieldDefinition,
  FieldPrimitiveType,
  fieldDefinitionSchema,
  KpiEntityType,
} from './field-definition.schema';

/**
 * Maps the freeform `IField.type: string` to a `FieldPrimitiveType`.
 * Mirrors the `columnTypeToDataType` helper for `ColumnMeta`. Unknown
 * variants fall back to `string` — same conservative behavior the
 * portco-columns mapper uses.
 */
export function ifieldTypeToPrimitive(type: string): FieldPrimitiveType {
  switch (type) {
    case 'boolean':
    case 'date':
    case 'string':
    case 'number':
      return type;
    case 'stringArray':
    case 'array':
      return FieldPrimitiveType.array;
    default:
      return FieldPrimitiveType.string;
  }
}

/**
 * Maps the legacy `IField.entity` (`'systemKPI'` / `'universalKPI'` /
 * `'userKPI'`) onto the schema's `KpiEntityType` enum. Other entity
 * values (e.g. `'company'`) shouldn't appear on KPI rows; if they do,
 * the helper returns `undefined` and the schema field stays unset.
 */
function ifieldEntityToKpiEntityType(entity: string | undefined): KpiEntityType | undefined {
  switch (entity) {
    case FieldEntity.systemKPI:
      return KpiEntityType.system;
    case FieldEntity.universalKPI:
      return KpiEntityType.universal;
    case FieldEntity.userKPI:
      return KpiEntityType.user;
    default:
      return undefined;
  }
}

/**
 * The BE serializes missing optional fields as `null`, but `IField`'s
 * TS type and `fieldDefinitionSchema`'s `.optional()` both model the
 * absent case as `undefined`. Coercing at the boundary keeps the schema
 * a strict contract (no `.nullable()` calls leaking `null` into the
 * canonical `FieldDefinition` type) and means new nullable BE fields
 * don't each need a one-off `?? undefined`.
 */
function stripNulls<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === null ? undefined : v])) as T;
}

/**
 * Inbound mapper: legacy KPI `IField` row → canonical `FieldDefinition`.
 * Terminates in `fieldDefinitionSchema.cast(...)` per the FD-2794
 * cross-cutting rule. Stripped-unknowns + the schema's allowed-format
 * test mean unrelated `IField` fields are dropped silently and bad
 * `(type, format)` combinations surface in tests, not in callers.
 */
export function kpiConfigToFieldDefinition(kc: IField<unknown>): FieldDefinition {
  return fieldDefinitionSchema.cast(
    stripNulls({
      key: kc.entityField,
      displayName: kc.displayName,
      description: kc.description,
      type: ifieldTypeToPrimitive(kc.type),
      format: kc.formMeta?.renderer?.type,
      sortOrder: kc.sortOrder,
      isActive: true,
      createdAt: kc.createdAt,
      updatedAt: '',
      kpiGroupId: kc.kpiGroupId ?? undefined,
      kpiScenario: kc.kpiScenario ?? undefined,
      periods: kc.periods ?? undefined,
      sectionType: kc.sectionType ?? undefined,
      yearCalcType: kc.yearCalcType ?? undefined,
      kpiEntityType: ifieldEntityToKpiEntityType(kc.entity),
    }),
    { stripUnknown: true }
  ) as FieldDefinition;
}

/**
 * `FieldDefinition` analogue of the legacy `isUniversalKPIField` in
 * `field2.data-model.ts`. Universal and system KPIs both render through
 * the same UI surface (read-only, system-managed); user KPIs are
 * customer-defined.
 */
export function isUniversalFieldDefinition(field: FieldDefinition): boolean {
  return field.kpiEntityType === KpiEntityType.universal || field.kpiEntityType === KpiEntityType.system;
}
